 import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Connection } from '@fry/connections/lib/connection';
import {
  EasFieldControl,
  EasFieldType,
  EasFormGroupEmpty,
  EasFormGroupState,
  EasFormService
} from "@fry/components/common/easform";
import { Validators } from "@angular/forms";
import { map, tap } from "rxjs/operators";
import { UserFieldsStore } from "@fry/lib/user-fields";
import { NotifyService } from "@fry/lib/utils";
import { ConnectionsStore } from "@fry/connections/lib/connections.store";
import { FieldValidators } from "@fry/lib/forms/validators";

@Component({
  selector: 'eascreate-or-update-dialog',
  templateUrl: './create-or-update-connections-dialog.component.html',
  standalone: false
})
export class CreateOrUpdateConnectionsDialogComponent implements OnInit {
  public mode: 'create' | 'update' | 'replace' = 'create';
  public connection: Connection;
  private connectionForm = inject(EasFormService);
  public $connectionForm = signal<EasFormGroupState>(EasFormGroupEmpty);

  constructor(
    private dialogRef: MatDialogRef<CreateOrUpdateConnectionsDialogComponent>,
    private userFieldsStore: UserFieldsStore,
    private notify: NotifyService,
    private connectionsStore: ConnectionsStore,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.connection = data.connection;
    this.mode = data.mode;
  }

  ngOnInit() {
    this.userFieldsStore.allUnique().pipe(
      map(fields => fields.map(field => ({ id: field.doc?._id, name: field.doc?.name }))),
      tap(userFieldsOptions => {
        let connectionFields: EasFieldControl[] = [];

        const isMode = {
          create: this.mode === 'create',
          update: this.mode === 'update',
          replace: this.mode === 'replace'
        };

        if (isMode.create || isMode.replace) {
          connectionFields.push({
            id: 'host',
            type: EasFieldType.Field,
            title: $localize`risr/assess instance URL`,
            widget: { type: 'string' },
            options: { required: true },
            validators: [Validators.required, FieldValidators.httpUrlValidator()],
          });
        }

        if (isMode.create || isMode.update) {
          connectionFields.push({
            id: 'userFieldId',
            type: EasFieldType.Field,
            title: $localize`Select the user field which populates the risr/assess candidate identifier`,
            widget: { type: 'select' },
            options: {
              required: true,
              categories: userFieldsOptions
            },
            validators: [Validators.required],
          });
        }

        this.$connectionForm.set({
          state: 'loaded',
          group: this.connectionForm.createForm(connectionFields, this.connection)
        });
      })
    ).subscribe();
  }

  onInitRegistration() {
    const formState = this.$connectionForm();
      if (formState.state !== 'loaded' || formState.group === undefined) {
        throw new Error('General form not loaded');
      }
      const form = formState.group;

      if (form.control.invalid) {
        this.notify.error($localize `Please review the form`);
      }

      Object.assign(this.connection.doc, form.getValue());

      let registerFunction = null;
      if (this.mode === 'create') {
        registerFunction = this.connectionsStore.initRegistration(this.connection);
      } else {
        registerFunction = this.connectionsStore.reconnect(this.connection.id, this.connection.doc.host);
      }

      return registerFunction.subscribe({
        next: (response) => {
          this.notify.success($localize`Registration initiated successfully, please wait you are going to be redirected to the registration page`);
          if (response.redirect_url) {
            setTimeout(() => {
              window.open(response.redirect_url, '_blank');
            }, 2000); // redirect in 2 seconds
          }

          this.dialogRef.close();
        },
        error: (error: any) => this.notify.error(error),
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onUpdateConnection() {
    const formState = this.$connectionForm();
      if (formState.state !== 'loaded' || formState.group === undefined) {
        throw new Error('General form not loaded');
      }
      const form = formState.group;

      if (form.control.invalid) {
        this.notify.error($localize `Please review the form`);
      }

      Object.assign(this.connection.doc, form.getValue());
      return this.connectionsStore.update(this.connection.id, this.connection.doc.userFieldId).subscribe({
        next: () => {
          this.notify.success($localize`Connection updated successfully`);
          this.dialogRef.close();
        },
        error: (error) => this.notify.error(error),
      });
  }
}
